import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Form,
  Modal,
  Alert,
  Row,
  Col,
  InputGroup,
  ProgressBar,
} from "react-bootstrap";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import Loader from "../../components/common/Loader";
import {
  PersonPlus,
  Pencil,
  Trash,
  Eye,
  Person,
  Shield,
  Search,
  Filter,
  Calendar,
  Lock,
  Unlock,
  SortDown,
  Envelope,
  Key,
  CheckCircle,
  XCircle,
  Clock,
  PersonCheck,
  PersonX,
} from "react-bootstrap-icons";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  // Selected user states
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    fullName: "",
    password: "",
    role: "counselor",
    permissions: [],
    isActive: true,
  });

  const [editFormData, setEditFormData] = useState({
    username: "",
    email: "",
    fullName: "",
    role: "counselor",
    permissions: [],
    isActive: true,
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordFields, setPasswordFields] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const { adminData } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/users");

      if (response.data.success) {
        setUsers(response.data.admins);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setError("");
      const response = await api.post("/admin/users", formData);

      if (response.data.success) {
        setSuccess("User created successfully");
        fetchUsers();
        setShowCreateModal(false);
        resetForm();
      }
    } catch (error) {
      console.error("Error creating user:", error);
      setError(error.response?.data?.error || "Failed to create user");
    }
  };

  const handleUpdate = async () => {
    try {
      setError("");
      // Prepare payload
      const payload = { ...editFormData };

      // If password change requested, validate on client
      if (showChangePassword) {
        if (passwordFields.newPassword !== passwordFields.confirmPassword) {
          setError("New passwords do not match");
          return;
        }
        if (passwordFields.newPassword.length < 6) {
          setError("Password must be at least 6 characters");
          return;
        }

        // If updating own account, include currentPassword
        const isSelf =
          adminData && selectedUser && adminData._id === selectedUser._id;
        if (isSelf) payload.currentPassword = passwordFields.currentPassword;
        payload.newPassword = passwordFields.newPassword;
      }

      const response = await api.put(
        `/admin/users/${selectedUser._id}`,
        payload,
      );

      if (response.data.success) {
        setSuccess("User updated successfully");
        fetchUsers();
        setShowEditModal(false);
        // reset password UI
        setShowChangePassword(false);
        setPasswordFields({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      }
    } catch (error) {
      console.error("Error updating user:", error);
      setError(error.response?.data?.error || "Failed to update user");
    }
  };

  const handleDelete = async () => {
    try {
      setError("");
      const response = await api.delete(`/admin/users/${selectedUser._id}`);

      if (response.data.success) {
        setSuccess("User deleted successfully");
        fetchUsers();
        setShowDeleteModal(false);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setError(error.response?.data?.error || "Failed to delete user");
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      email: "",
      fullName: "",
      password: "",
      role: "counselor",
      permissions: [],
      isActive: true,
    });
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "super_admin":
        return (
          <Badge bg="danger" className="px-3 py-2 rounded-pill">
            <Shield className="me-1" /> Super Admin
          </Badge>
        );
      case "counselor":
        return (
          <Badge bg="primary" className="px-3 py-2 rounded-pill">
            <Person className="me-1" /> Counselor
          </Badge>
        );
      default:
        return (
          <Badge bg="secondary" className="px-3 py-2 rounded-pill">
            {role}
          </Badge>
        );
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success" className="px-3 py-2 rounded-pill">
        <CheckCircle className="me-1" /> Active
      </Badge>
    ) : (
      <Badge bg="secondary" className="px-3 py-2 rounded-pill">
        <XCircle className="me-1" /> Inactive
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditFormData({
      username: user.username,
      email: user.email,
      fullName: user.fullName || "",
      role: user.role,
      permissions: user.permissions || [],
      isActive: user.isActive,
    });
    setShowEditModal(true);
  };

  const handleViewClick = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Filter users
  const filteredUsers = users.filter((user) => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      if (
        !(
          user.username?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.fullName?.toLowerCase().includes(searchLower)
        )
      )
        return false;
    }

    // Role filter
    if (roleFilter !== "all" && user.role !== roleFilter) return false;

    // Status filter
    if (statusFilter !== "all") {
      if (statusFilter === "active" && !user.isActive) return false;
      if (statusFilter === "inactive" && user.isActive) return false;
    }

    return true;
  });

  // Calculate user statistics
  const userStats = {
    total: users.length,
    super_admin: users.filter((u) => u.role === "super_admin").length,
    counselor: users.filter((u) => u.role === "counselor").length,
    active: users.filter((u) => u.isActive).length,
    inactive: users.filter((u) => !u.isActive).length,
  };

  if (loading) return <Loader />;

  const pageStyle = {
    minHeight: "100vh",
    padding: "20px",
  };

  const cardStyle = {
    borderRadius: "15px",
    border: "none",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    background: "rgba(255,255,255,0.95)",
  };

  const statCardStyle = {
    borderRadius: "12px",
    border: "none",
    color: "white",
    padding: "20px",
  };

  return (
    <div style={pageStyle}>
      <div className="container-fluid">
        {/* Header */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1 fw-bold text-gradient">
                <Person className="me-3" size={32} />
                User Management
              </h2>
              <p className="text-muted mb-0">
                Manage system administrators and counselors
              </p>
            </div>
            <Button
              variant="primary"
              className="d-flex align-items-center px-4 py-2"
              onClick={() => setShowCreateModal(true)}
              style={{
                borderRadius: "10px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
              }}
            >
              <PersonPlus className="me-2" size={20} />
              Add New User
            </Button>
          </div>

          {/* Stats Cards */}
          <Row className="mb-4 g-3">
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Person size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">{userStats.total}</h2>
                      <p className="mb-0 opacity-75">Total Users</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PersonCheck size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">{userStats.active}</h2>
                      <p className="mb-0 opacity-75">Active Users</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PersonX size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">{userStats.inactive}</h2>
                      <p className="mb-0 opacity-75">Inactive Users</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #dc3545 0%, #e83e8c 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Shield size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">{userStats.super_admin}</h2>
                      <p className="mb-0 opacity-75">Super Admins</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Search and Filter Bar */}
          <Card className="mb-4" style={cardStyle}>
            <Card.Body className="p-4">
              <Row className="align-items-center g-3">
                <Col md={4}>
                  <div
                    className="input-group"
                    style={{ borderRadius: "10px", overflow: "hidden" }}
                  >
                    <span className="input-group-text bg-white border-end-0">
                      <Search size={20} className="text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder="Search by username, email, or name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ border: "none", fontSize: "16px" }}
                    />
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-center">
                    <Filter className="me-2 text-muted" size={20} />
                    <select
                      className="form-select"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      style={{ borderRadius: "10px" }}
                    >
                      <option value="all">All Roles</option>
                      <option value="super_admin">Super Admin</option>
                      <option value="counselor">Counselor</option>
                    </select>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-center">
                    <Filter className="me-2 text-muted" size={20} />
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{ borderRadius: "10px" }}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active Only</option>
                      <option value="inactive">Inactive Only</option>
                    </select>
                  </div>
                </Col>
                <Col md={2} className="text-end">
                  <Button
                    variant="outline-secondary"
                    style={{ borderRadius: "10px" }}
                    onClick={() => {
                      setSearchTerm("");
                      setRoleFilter("all");
                      setStatusFilter("all");
                    }}
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Alerts */}
          {error && (
            <Alert
              variant="danger"
              onClose={() => setError("")}
              dismissible
              className="border-0 shadow-sm mb-4"
            >
              <div className="d-flex align-items-center">
                <XCircle className="me-2" size={20} />
                <span>{error}</span>
              </div>
            </Alert>
          )}

          {success && (
            <Alert
              variant="success"
              onClose={() => setSuccess("")}
              dismissible
              className="border-0 shadow-sm mb-4"
            >
              <div className="d-flex align-items-center">
                <CheckCircle className="me-2" size={20} />
                <span>{success}</span>
              </div>
            </Alert>
          )}

          {/* Users Table */}
          <Card style={cardStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="fw-bold mb-1">
                    <Person className="me-2" />
                    System Users
                  </h5>
                  <p className="text-muted mb-0">
                    Showing {filteredUsers.length} of {userStats.total} users
                  </p>
                </div>
              </div>

              <div
                className="table-responsive"
                style={{ borderRadius: "10px", overflow: "hidden" }}
              >
                <Table hover className="mb-0">
                  <thead
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                    }}
                  >
                    <tr>
                      <th className="ps-4 py-3">
                        <div className="d-flex align-items-center">
                          <SortDown className="me-2" />
                          User
                        </div>
                      </th>
                      <th className="py-3">Email</th>
                      <th className="py-3">Role</th>
                      <th className="py-3">Status</th>
                      <th className="py-3">Activity</th>
                      <th className="py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr
                        key={user._id}
                        style={{
                          transition: "all 0.3s ease",
                          borderBottom: "1px solid #f1f1f1",
                        }}
                      >
                        <td className="ps-4">
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                background: user.isActive
                                  ? "linear-gradient(135deg, #28a74515 0%, #20c99715 100%)"
                                  : "linear-gradient(135deg, #6c757d15 0%, #49505715 100%)",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginRight: "12px",
                                color: user.isActive ? "#28a745" : "#6c757d",
                              }}
                            >
                              <Person size={20} />
                            </div>
                            <div>
                              <div className="fw-bold">{user.username}</div>
                              <div className="small text-muted">
                                {user.fullName || "No name provided"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="fw-medium">{user.email}</div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            {getRoleBadge(user.role)}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            {getStatusBadge(user.isActive)}
                          </div>
                        </td>
                        <td>
                          <div>
                            <div className="small text-muted">
                              Created: {formatDate(user.createdAt)}
                            </div>
                            <div className="small text-muted">
                              <Clock className="me-1" size={12} />
                              Last login:{" "}
                              {user.lastLogin
                                ? formatDate(user.lastLogin)
                                : "Never"}
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="d-flex align-items-center"
                              title="View Details"
                              onClick={() => handleViewClick(user)}
                              style={{ borderRadius: "8px" }}
                            >
                              <Eye />
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="d-flex align-items-center"
                              title="Edit User"
                              onClick={() => handleEditClick(user)}
                              style={{ borderRadius: "8px" }}
                            >
                              <Pencil />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="d-flex align-items-center"
                              title="Delete User"
                              onClick={() => handleDeleteClick(user)}
                              disabled={user.role === "super_admin"}
                              style={{ borderRadius: "8px" }}
                            >
                              <Trash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <div className="py-4">
                            <Search size={48} className="text-muted mb-3" />
                            <h5 className="text-muted">No users found</h5>
                            <p className="text-muted mb-0">
                              Try adjusting your filters
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Create User Modal */}
      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  marginRight: "15px",
                }}
              >
                <PersonPlus size={24} />
              </div>
              <div>
                Create New User
                <div className="small text-muted mt-1">
                  Add a new admin or counselor account
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            <Row className="mb-3 g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Person className="me-1" />
                    Username *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                    placeholder="Enter username"
                    required
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Envelope className="me-1" />
                    Email *
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="Enter email"
                    required
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3 g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.fullName}
                    onChange={(e) =>
                      setFormData({ ...formData, fullName: e.target.value })
                    }
                    placeholder="Enter full name"
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Key className="me-1" />
                    Password *
                  </Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    placeholder="Enter password"
                    required
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3 g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Shield className="me-1" />
                    Role
                  </Form.Label>
                  <Form.Select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  >
                    <option value="counselor">Counselor</option>
                    <option value="super_admin">Super Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Lock className="me-1" />
                    Status
                  </Form.Label>
                  <Form.Select
                    value={formData.isActive}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isActive: e.target.value === "true",
                      })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label className="fw-medium">
                <Shield className="me-1" />
                Permissions
              </Form.Label>
              <div className="row">
                {[
                  "read",
                  "write",
                  "delete",
                  "manage_users",
                  "manage_settings",
                ].map((permission) => (
                  <div key={permission} className="col-md-6 mb-2">
                    <Form.Check
                      type="checkbox"
                      label={
                        <span className="text-capitalize">
                          {permission.replace("_", " ")}
                        </span>
                      }
                      checked={formData.permissions.includes(permission)}
                      onChange={(e) => {
                        const newPermissions = e.target.checked
                          ? [...formData.permissions, permission]
                          : formData.permissions.filter(
                              (p) => p !== permission,
                            );
                        setFormData({
                          ...formData,
                          permissions: newPermissions,
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={() => setShowCreateModal(false)}
            className="px-4"
            style={{ borderRadius: "10px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            className="px-4"
            style={{
              borderRadius: "10px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
            }}
          >
            Create User
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  background:
                    "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  marginRight: "15px",
                }}
              >
                <Pencil size={24} />
              </div>
              <div>
                Edit User: {selectedUser?.username}
                <div className="small text-muted mt-1">
                  Update user details and permissions
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            <Row className="mb-3 g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Person className="me-1" />
                    Username
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.username}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        username: e.target.value,
                      })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Envelope className="me-1" />
                    Email
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={editFormData.email}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        email: e.target.value,
                      })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3 g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.fullName}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        fullName: e.target.value,
                      })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Shield className="me-1" />
                    Role
                  </Form.Label>
                  <Form.Select
                    value={editFormData.role}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, role: e.target.value })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  >
                    <option value="counselor">Counselor</option>
                    <option value="super_admin">Super Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Check
                  type="checkbox"
                  id="changePasswordToggle"
                  label="Change password"
                  checked={showChangePassword}
                  onChange={(e) => setShowChangePassword(e.target.checked)}
                />
              </Col>
            </Row>

            {showChangePassword && (
              <Row className="mb-3 g-3">
                {/* If updating own account, require current password */}
                {adminData &&
                  selectedUser &&
                  adminData._id === selectedUser._id && (
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium">
                          Current Password
                        </Form.Label>
                        <Form.Control
                          type="password"
                          value={passwordFields.currentPassword}
                          onChange={(e) =>
                            setPasswordFields({
                              ...passwordFields,
                              currentPassword: e.target.value,
                            })
                          }
                          className="py-2"
                          style={{ borderRadius: "8px" }}
                        />
                      </Form.Group>
                    </Col>
                  )}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-medium">New Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordFields.newPassword}
                      onChange={(e) =>
                        setPasswordFields({
                          ...passwordFields,
                          newPassword: e.target.value,
                        })
                      }
                      className="py-2"
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-medium">
                      Confirm New Password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordFields.confirmPassword}
                      onChange={(e) =>
                        setPasswordFields({
                          ...passwordFields,
                          confirmPassword: e.target.value,
                        })
                      }
                      className="py-2"
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3 g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Lock className="me-1" />
                    Status
                  </Form.Label>
                  <Form.Select
                    value={editFormData.isActive}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        isActive: e.target.value === "true",
                      })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label className="fw-medium">
                <Shield className="me-1" />
                Permissions
              </Form.Label>
              <div className="row">
                {[
                  "read",
                  "write",
                  "delete",
                  "manage_users",
                  "manage_settings",
                ].map((permission) => (
                  <div key={permission} className="col-md-6 mb-2">
                    <Form.Check
                      type="checkbox"
                      label={
                        <span className="text-capitalize">
                          {permission.replace("_", " ")}
                        </span>
                      }
                      checked={editFormData.permissions.includes(permission)}
                      onChange={(e) => {
                        const newPermissions = e.target.checked
                          ? [...editFormData.permissions, permission]
                          : editFormData.permissions.filter(
                              (p) => p !== permission,
                            );
                        setEditFormData({
                          ...editFormData,
                          permissions: newPermissions,
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={() => setShowEditModal(false)}
            className="px-4"
            style={{ borderRadius: "10px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdate}
            className="px-4"
            style={{
              borderRadius: "10px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
            }}
          >
            Update User
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View User Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          {selectedUser && (
            <div>
              <div className="text-center mb-4">
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    margin: "0 auto 15px",
                  }}
                >
                  <Person size={36} />
                </div>
                <h4>{selectedUser.fullName || selectedUser.username}</h4>
                <p className="text-muted">{selectedUser.email}</p>
              </div>

              <div
                className="p-3 mb-3"
                style={{
                  background: "#f8f9fa",
                  borderRadius: "10px",
                  borderLeft: "4px solid #4361ee",
                }}
              >
                <div className="row g-3">
                  <div className="col-6">
                    <small className="text-muted d-block">Username</small>
                    <div className="fw-medium">{selectedUser.username}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-block">Role</small>
                    <div>{getRoleBadge(selectedUser.role)}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-block">Status</small>
                    <div>{getStatusBadge(selectedUser.isActive)}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-block">Created</small>
                    <div className="fw-medium">
                      {formatDate(selectedUser.createdAt)}
                    </div>
                  </div>
                  <div className="col-12">
                    <small className="text-muted d-block">Last Login</small>
                    <div className="fw-medium d-flex align-items-center">
                      <Clock className="me-2" size={16} />
                      {selectedUser.lastLogin
                        ? formatDate(selectedUser.lastLogin)
                        : "Never"}
                    </div>
                  </div>
                </div>
              </div>

              {selectedUser.permissions &&
                selectedUser.permissions.length > 0 && (
                  <div className="mb-3">
                    <div className="fw-medium mb-2">Permissions:</div>
                    <div className="d-flex flex-wrap gap-1">
                      {selectedUser.permissions.map((permission, index) => (
                        <Badge
                          key={index}
                          bg="info"
                          className="px-3 py-1 rounded-pill"
                        >
                          {permission}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={() => setShowViewModal(false)}
            className="px-4"
            style={{ borderRadius: "10px" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="text-center mb-3">
            <Trash size={48} className="text-danger mb-3" />
            <h5>Delete User Account</h5>
          </div>
          <p className="text-center">
            Are you sure you want to delete user{" "}
            <strong className="text-danger">{selectedUser?.username}</strong>?
          </p>
          <p className="text-center text-muted small mb-0">
            This action cannot be undone. All user data will be permanently
            removed.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={() => setShowDeleteModal(false)}
            className="px-4"
            style={{ borderRadius: "10px" }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            className="px-4"
            style={{ borderRadius: "10px" }}
          >
            <Trash className="me-1" /> Delete User
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserManagement;
